import React, { FC, useEffect, useState } from 'react';

import { ResourceMapping } from '@ariksa/inventory-core/api';
import { Box, Center, HStack, Stack, useDisclosure } from '@chakra-ui/react';
import { each } from 'lodash';
import map from 'lodash/map';
import { useSelector } from 'react-redux';
import { customTheme } from 'theme';

import {
  renderCloudIcon,
  StackedCell,
  StackedHeader,
} from 'components/DataDisplay';
import { CustomTable2 as Table } from 'components/DataDisplay/NewTable/Table';
import { CancelButton, PrimaryButton } from 'components/DataEntry';
import { EnvironmentIcon } from 'components/Icons';
import { Drawer } from 'components/Overlay';
import { useAccessBoundary } from 'containers/App/hooks/useAccessBoundary';
import { useResourceType } from 'containers/App/hooks/useResourceType';
import { EnvironmentName } from 'containers/App/utils';
import { ExcludeResourcesConfirmationModal } from 'containers/Findings/Alerts/Components/AlertsTable/ExcludeMultipleResources/ExcludeResourceConfirmationModal';
import { useIndividualAlertsTableColumns } from 'containers/Findings/Alerts/Components/AlertsTable/hooks/useIndividualAlertsTableColumns';
import { renderFindings } from 'containers/Findings/Alerts/Components/AlertsTable/utils';
import { selectAlerts } from 'containers/Findings/Alerts/selectors';
import { useCloudAccountId } from 'containers/Setup/CloudAccounts/utils';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  type: string;
  selectedResources: string[];
  setSelectedResources: (vals: string[]) => void;
  renderName({ row });
  onRowClick: (row: Record<string, any>) => void;
}

export const ExcludeMultipleResourcesTableDrawer: FC<Props> = props => {
  const {
    isOpen,
    onClose,
    type,
    selectedResources,
    renderName,
    onRowClick,
  } = props;
  const { environmentId } = useAccessBoundary();
  const { getResourceType } = useResourceType();
  const { toCloudAccountId } = useCloudAccountId();
  const [resourceType, setResourceType] = useState<ResourceMapping>(
    {} as ResourceMapping,
  );
  const [resourceMapping, setResourceMapping] = useState<Record<string, any>>(
    {},
  );
  const [blueprints, setBlueprints] = useState<Record<string, any>[]>([]);
  const [noOfAccounts, setNoOfAccounts] = useState<number>(0);
  const { context } = useIndividualAlertsTableColumns();
  const { accountMapping } = useCloudAccountId();
  const { aggregatedFindings } = useSelector(selectAlerts);
  const excludeConfirmation = useDisclosure();

  useEffect(() => {
    let items: Record<string, any> = {};
    each(aggregatedFindings.data, o => (items[o?.entity_uuid] = o));
    setResourceMapping(items);
  }, [aggregatedFindings.data]);

  useEffect(() => {
    setResourceType(getResourceType(type) || ({} as ResourceMapping));
  }, [getResourceType, type]);

  useEffect(() => {
    let items: Record<string, any> = {};
    let accounts = new Set<string>();
    each(selectedResources, o => {
      const item = resourceMapping[o];
      accounts.add(item?.account ?? item?.account_id);
      each(item?.blueprints, o => {
        if (!items[o?.blueprint_id]) items[o?.blueprint_id] = o;
      });
    });
    setBlueprints(Object.values(items));
    setNoOfAccounts(accounts.size);
  }, [selectedResources, resourceMapping]);

  const columns = [
    {
      header: (
        <Box pl={24} ml={1}>
          Name
        </Box>
      ),
      accessor: 'entity_name',
      align: 'left',
      render: renderName,
    },
    {
      header: 'Cloud',
      render: ({ row }) => (
        <Center>
          {renderCloudIcon({
            provider: accountMapping?.[row?.account_id]?.cloud_type,
          })}
        </Center>
      ),
    },
    {
      header: 'Deployed In',
      accessor: 'account_id',
      render: ({ value }) => {
        return (
          <StackedCell
            upper={<EnvironmentName environmentId={environmentId!} />}
            lower={toCloudAccountId(value)}
          />
        );
      },
      align: 'left',
      styles: { width: 100 },
    },
    {
      header: 'Severity',
      accessor: 'severity',
      render: renderFindings,
      align: 'left',
      styles: { width: 100 },
    },
    context,
  ];

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      header={
        <StackedHeader
          upper="Exclude Multiple Resources from All Policies"
          lower={
            resourceType?.cloud_native_name +
            ' / ' +
            resourceType?.cloud_agnostic_name
          }
          type={resourceType?.cloud_native_class!}
        />
      }
      body={
        <Stack h="full" pt={4} spacing={6}>
          <HStack>
            <Box color={customTheme.colors.gray['300']}>By Framework</Box>
            <Box fontWeight={600}>
              {map(blueprints, o => o?.blueprint_name)?.join(', ')}
            </Box>
            <Box color={customTheme.colors.gray['300']}>attached to</Box>
            <HStack fontWeight={600}>
              <Center boxSize={5} color="primary">
                <EnvironmentIcon />
              </Center>
              <EnvironmentName environmentId={environmentId!} />
            </HStack>
          </HStack>
          <Box flex={1} h="full">
            <Table
              columns={columns}
              data={map(selectedResources, o => resourceMapping[o])}
              onRowClick={onRowClick}
            />
          </Box>
          {excludeConfirmation.isOpen && (
            <ExcludeResourcesConfirmationModal
              isOpen={excludeConfirmation.isOpen}
              onClose={excludeConfirmation.onClose}
              onSuccess={onClose}
              resourceType={resourceType}
              resourceIds={selectedResources}
              blueprintIds={map(blueprints, o => o.blueprint_id)}
              noOfAccounts={noOfAccounts}
            />
          )}
        </Stack>
      }
      footer={
        <HStack w="full" justify="space-between">
          <CancelButton onClick={() => onClose()}>CANCEL</CancelButton>
          <PrimaryButton
            px={7}
            isDisabled={!selectedResources?.length}
            onClick={() => excludeConfirmation.onOpen()}
          >
            OK
          </PrimaryButton>
        </HStack>
      }
      styles={{ content: { maxW: '1100px' } }}
    />
  );
};
